import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiBase, apiStream, ApiError } from "../lib/api";
import { useOrg } from "../lib/session";
import { useWorkspace } from "../lib/workspace";
import type { Agent, AgentTemplate, Behaviour, Member, SetupDraft, SetupPage } from "../lib/types";
import { useEntitlements } from "../lib/plans";
import { Empty, ErrorText, Field, Loading, PageHeader } from "../components/ui";
import { AgentBuilder, behaviourOf, cleanBehaviour, emptyBehaviour, placeholders } from "../components/AgentBuilder";
import { TestLab } from "../components/TestLab";

const purposeLabel: Record<string, string> = { sales: "Sales", booking: "Bookings", support: "Support", general: "General" };

/** statusLabel names an agent's status in plain words. */
export const statusLabel: Record<string, string> = { active: "Live", paused: "Paused", draft: "Draft" };

export function Agents() {
  const { base, orgId } = useOrg();
  const agents = useQuery({ queryKey: [orgId, "agents"], queryFn: () => api<{ agents: Agent[] }>(`${base}/agents`) });
  const stats = useQuery({ queryKey: [orgId, "agents", "stats"], queryFn: () => api<{ agents: { agent_id: string; conversations: number; replies: number; leads: number; handoffs: number }[] }>(`${base}/agents/stats`), staleTime: 60_000 });
  const plan = useEntitlements();
  const max = plan.limit("ai_agents");
  const count = agents.data?.agents.length ?? 0;
  return (
    <>
      <PageHeader
        title="AI agents"
        actions={max === undefined || count < max ? <Link className="button" to={`/o/${orgId}/agents/new`}>New agent</Link> : <span className="muted small">Agent limit reached ({max})</span>}
      />
      {agents.isLoading ? <Loading /> : agents.error ? <ErrorText error={agents.error} /> : count === 0 ? (
        <section className="card">
          <h2>Build your first agent</h2>
          <p>Start from your website and SVENEX drafts the agent for you, or pick an industry template.</p>
          <Link className="button" to={`/o/${orgId}/agents/new`}>Get started</Link>
        </section>
      ) : (
        <div className="cards">
          {agents.data!.agents.map((a) => {
            const st = stats.data?.agents.find((x) => x.agent_id === a.id);
            const twin = agents.data!.agents.some((b) => b.id !== a.id && b.name.trim().toLowerCase() === a.name.trim().toLowerCase());
            return (
              <Link key={a.id} className="card agent-card" to={`/o/${orgId}/agents/${a.id}`}>
                <span className="inline"><b>{a.name}</b><span className={`status ${a.status}`}>{statusLabel[a.status] ?? a.status}</span></span>
                <span className="muted small">{purposeLabel[a.purpose] ?? a.purpose}{a.template_name ? ` · ${a.template_name}` : ""}</span>
                {st && (
                  <span className="agent-stats small">
                    <span><b>{st.conversations.toLocaleString()}</b> chats</span>
                    <span><b>{st.replies.toLocaleString()}</b> replies</span>
                    <span><b>{st.leads.toLocaleString()}</b> leads</span>
                    <span><b>{st.handoffs.toLocaleString()}</b> handovers</span>
                  </span>
                )}
                {st && <span className="muted small">Last 30 days</span>}
                {placeholders(a.instructions).length > 0 && <span className="warn-text small">{placeholders(a.instructions).length} details to fill in</span>}
                {twin && <span className="warn-text small">Another agent has the same name. Rename one so your team can tell them apart.</span>}
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}

type Step = { kind: "start" } | { kind: "website" } | { kind: "templates" } | { kind: "review"; templateKey?: string; pages?: SetupPage[]; source: string };

export function AgentNew() {
  const { base, orgId } = useOrg();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const plan = useEntitlements();
  const [step, setStep] = useState<Step>({ kind: "start" });
  const [value, setValue] = useState<Behaviour>(emptyBehaviour);
  const [importPages, setImportPages] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const templates = useQuery({ queryKey: [orgId, "agent-templates"], queryFn: () => api<{ templates: AgentTemplate[] }>(`${base}/agent-templates`) });
  const ws = useWorkspace();
  const recommended = templates.data?.templates.find((t) => t.key === ws.data?.agent_template_key);
  const limit = (k: string) => plan.limit(k) ?? undefined;
  const canImport = plan.enabled("knowledge_base") && plan.enabled("knowledge_web_pages");

  function pickTemplate(t: AgentTemplate | null) {
    setValue(t ? { ...behaviourOf(t), name: value.name, language: "auto" } : { ...emptyBehaviour, name: value.name });
    setStep({ kind: "review", templateKey: t?.key, source: t ? `Template: ${t.name}` : "Blank agent" });
  }

  async function create() {
    setSaving(true);
    setError(null);
    try {
      const review = step as Extract<Step, { kind: "review" }>;
      const agent = await api<Agent>(`${base}/agents`, { method: "POST", json: { ...cleanBehaviour(value), ...(review.templateKey && { template_key: review.templateKey }) } });
      if (review.pages && importPages && canImport) {
        for (const p of review.pages) {
          try {
            await api(`${base}/knowledge/sources`, { method: "POST", json: { title: p.title.slice(0, 200), url: p.url } });
          } catch (err) {
            if (!(err instanceof ApiError && err.code === "duplicate_source")) console.warn(err);
          }
        }
      }
      qc.invalidateQueries({ queryKey: [orgId] });
      navigate(`/o/${orgId}/agents/${agent.id}`);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageHeader title="New agent" actions={<Link to={`/o/${orgId}/agents`}>Cancel</Link>} />
      {step.kind === "start" && (
        <div className="cards">
          {recommended && (
            <button className="card choice" onClick={() => pickTemplate(recommended)}>
              <b>Recommended: {recommended.name}</b>
              <span className="muted">The starting point set up for your business. Fill in your details and you're ready.</span>
            </button>
          )}
          {plan.enabled("agent_website_setup") && (
            <button className="card choice" onClick={() => setStep({ kind: "website" })}>
              <b>From your website</b>
              <span className="muted">SVENEX reads your site and drafts the agent: services, prices, hours, questions to ask. Recommended.</span>
            </button>
          )}
          <button className="card choice" onClick={() => setStep({ kind: "templates" })}>
            <b>From an industry template</b>
            <span className="muted">Start from a playbook for your type of business, then fill in your details.</span>
          </button>
          <button className="card choice" onClick={() => pickTemplate(null)}>
            <b>Blank</b>
            <span className="muted">Write everything yourself.</span>
          </button>
        </div>
      )}
      {step.kind === "website" && (
        <WebsiteSetup
          onDraft={(d, pages) => {
            setValue({ ...emptyBehaviour, ...behaviourOf(d as unknown as Partial<Behaviour>), language: "auto" });
            setStep({ kind: "review", templateKey: d.template_key || undefined, pages, source: d.ai_written ? "Drafted from your website" : "Drafted from your website (basic mode: add an AI key for a full draft)" });
          }}
          onBack={() => setStep({ kind: "start" })}
        />
      )}
      {step.kind === "templates" && (
        <TemplateGallery templates={templates.data?.templates} loading={templates.isLoading} onPick={pickTemplate} onBack={() => setStep({ kind: "start" })} />
      )}
      {step.kind === "review" && (
        <>
          <p className="muted">{step.source}. Review everything below, then create the agent. You can test it right after.</p>
          {placeholders(value.instructions).length > 0 && (
            <p className="notice small">Fill in the details in [square brackets] where you can. The agent says it doesn't know anything still in brackets.</p>
          )}
          <section className="card">
            <AgentBuilder value={value} onChange={setValue} limit={limit} leadCaptureAllowed={plan.enabled("lead_capture")} handoffAllowed={plan.enabled("human_handoff")} dealsAllowed={plan.enabled("deals")} bookingAllowed={plan.enabled("bookings")} />
          </section>
          {step.pages && step.pages.length > 0 && (
            <section className="card">
              <h2>Pages read from your website</h2>
              <ul className="list">{step.pages.map((p) => <li key={p.url}>{p.title} <span className="muted small">{p.url}</span></li>)}</ul>
              {canImport ? (
                <label className="inline"><input type="checkbox" checked={importPages} onChange={(e) => setImportPages(e.target.checked)} /> Add these pages to the knowledge base so the agent can look up details</label>
              ) : <p className="muted small">Importing web pages into knowledge isn't included in your plan.</p>}
            </section>
          )}
          <ErrorText error={error} />
          <div className="actions">
            <button onClick={create} disabled={saving || !value.name.trim()}>{saving ? "Creating…" : "Create agent"}</button>
            <button className="secondary" onClick={() => setStep({ kind: "start" })}>Start over</button>
          </div>
        </>
      )}
    </>
  );
}

function WebsiteSetup({ onDraft, onBack }: { onDraft: (d: SetupDraft, pages: SetupPage[]) => void; onBack: () => void }) {
  const { base } = useOrg();
  const [url, setUrl] = useState("");
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  async function start(e: React.FormEvent) {
    e.preventDefault();
    setRunning(true);
    setError(null);
    setLog([]);
    try {
      await apiStream(`${base}/agents/setup-from-website`, { url }, (event, d) => {
        if (event === "status") setLog((l) => [...l, d.message]);
        else if (event === "page") setLog((l) => [...l, `Read: ${d.title}`]);
        else if (event === "error") setError(new ApiError(0, "stream_error", d.message));
        else if (event === "draft") onDraft(d.draft, d.pages);
      });
    } catch (err) {
      setError(err);
    } finally {
      setRunning(false);
    }
  }
  return (
    <section className="card">
      <h2>Set up from your website</h2>
      <form className="form" onSubmit={start}>
        <Field label="Website address"><input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="www.yourbusiness.com" required disabled={running} /></Field>
        <div className="actions">
          <button type="submit" disabled={running}>{running ? "Reading your website…" : "Draft my agent"}</button>
          <button type="button" className="secondary" onClick={onBack} disabled={running}>Back</button>
        </div>
      </form>
      {log.length > 0 && <ul className="progress">{log.map((l, i) => <li key={i}>{l}</li>)}</ul>}
      <ErrorText error={error} />
    </section>
  );
}

function TemplateGallery({ templates, loading, onPick, onBack }: { templates?: AgentTemplate[]; loading: boolean; onPick: (t: AgentTemplate) => void; onBack: () => void }) {
  const groups = useMemo(() => {
    const m = new Map<string, AgentTemplate[]>();
    templates?.forEach((t) => m.set(t.industry, [...(m.get(t.industry) ?? []), t]));
    return [...m.entries()];
  }, [templates]);
  if (loading) return <Loading />;
  return (
    <>
      <button className="link" onClick={onBack}>← Back</button>
      {groups.length === 0 && <Empty>No templates are available yet.</Empty>}
      {groups.map(([industry, list]) => (
        <section key={industry}>
          <h3>{industry}</h3>
          <div className="cards">
            {list.map((t) => (
              <button key={t.id} className="card choice" onClick={() => onPick(t)}>
                <b>{t.name}</b>
                <span className="muted small">{t.description}</span>
              </button>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

export function AgentPage() {
  const { agentId } = useParams();
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const key = [orgId, "agent", agentId];
  const agent = useQuery({ queryKey: key, queryFn: () => api<Agent>(`${base}/agents/${agentId}`) });
  const members = useQuery({ queryKey: [orgId, "members"], queryFn: () => api<{ members: Member[] }>(`${base}/members`) });
  const plan = useEntitlements();
  const [value, setValue] = useState<Behaviour | null>(null);
  const [status, setStatus] = useState("active");
  const [handoff, setHandoff] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (agent.data) {
      setValue(behaviourOf(agent.data));
      setStatus(agent.data.status);
      setHandoff(agent.data.handoff_member_id ?? "");
    }
  }, [agent.data]);

  const dirty = useMemo(() => {
    if (!agent.data || !value) return false;
    return JSON.stringify(cleanBehaviour(value)) !== JSON.stringify(cleanBehaviour(behaviourOf(agent.data))) || status !== agent.data.status || handoff !== (agent.data.handoff_member_id ?? "");
  }, [agent.data, value, status, handoff]);

  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (dirty) e.preventDefault(); };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  if (agent.isLoading || !value) return agent.error ? <ErrorText error={agent.error} /> : <Loading />;
  const a = agent.data!;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const out = await api<Agent>(`${base}/agents/${agentId}`, { method: "PATCH", json: { ...cleanBehaviour(value!), status, ...(handoff && { handoff_member_id: handoff }) } });
      qc.setQueryData(key, { ...out, widgets: a.widgets });
      qc.invalidateQueries({ queryKey: [orgId, "agents"] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageHeader
        title={a.name}
        actions={
          <>
            {dirty && <span className="warn-text small">Unsaved changes</span>}
            {saved && <span className="ok-text small">Saved</span>}
            <button onClick={save} disabled={saving || !dirty}>{saving ? "Saving…" : "Save"}</button>
          </>
        }
      />
      <p className="muted small">
        {a.template_name ? `Started from the ${a.template_name} template. ` : ""}
        {a.has_operator_instructions ? "The SVENEX team has also tuned this agent for you." : ""}
      </p>
      <ErrorText error={error} />
      <div className="builder-layout">
        <section className="card">
          <AgentBuilder
            value={value}
            onChange={setValue}
            limit={(k) => plan.limit(k) ?? undefined}
            leadCaptureAllowed={plan.enabled("lead_capture")}
            handoffAllowed={plan.enabled("human_handoff")} dealsAllowed={plan.enabled("deals")} bookingAllowed={plan.enabled("bookings")}
            knowledge={<KnowledgeNote />}
            top={
              <div className="row">
                <Field label="Status">
                  <select value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="active">Live: answering visitors</option>
                    <option value="paused">Paused</option>
                    <option value="draft">Draft</option>
                  </select>
                </Field>
                <Field label="Leads and handovers go to">
                  <select value={handoff} onChange={(e) => setHandoff(e.target.value)}>
                    <option value="">Unassigned queue</option>
                    {members.data?.members.filter((m) => m.status === "active" && m.crm_role).map((m) => <option key={m.id} value={m.id}>{m.display_name}</option>)}
                  </select>
                </Field>
              </div>
            }
          />
        </section>
        <div className="lab-column">
          {plan.enabled("agent_test_lab") ? (
            <TestLab base={base} agentId={a.id} draft={cleanBehaviour(value)} orgId={orgId} />
          ) : (
            <section className="card"><h2>Test lab</h2><p className="muted">The test lab isn't included in your plan.</p></section>
          )}
        </div>
      </div>
      <section className="card inline-card">
        <div><b>Put {a.name} on your website or WhatsApp</b><p className="muted small">The chat link, the website code and WhatsApp numbers are all in Channels.</p></div>
        <Link className="button-link secondary" to={`/o/${orgId}/channels`}>Open Channels</Link>
      </section>
    </>
  );
}

/** KnowledgeNote: what the agent can also look up, from the company's Knowledge. */
function KnowledgeNote() {
  const { base, orgId, allows } = useOrg();
  const plan = useEntitlements();
  const on = allows("knowledge") && plan.enabled("knowledge_base");
  const q = useQuery({ queryKey: [orgId, "sources"], queryFn: () => api<{ sources: { id: string }[] }>(`${base}/knowledge/sources`), enabled: on });
  if (!on) return <p className="muted small">Long documents (menus, price lists, FAQs) go in Knowledge, which isn't part of your plan.</p>;
  const n = q.data?.sources.length;
  return (
    <p className="muted small">
      For long documents (menus, price lists, FAQs), use <Link to={`/o/${orgId}/knowledge`}>Knowledge</Link>: every agent looks up the relevant parts for each question.
      {n !== undefined && <> {n === 0 ? "Nothing added yet." : `${n} source${n === 1 ? "" : "s"} added.`}</>}
    </p>
  );
}

export function Widgets({ agent: a, bare = false }: { agent: Agent; bare?: boolean }) {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const key = [orgId, "agent", a.id];
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const w = a.widgets?.[0];
  if (!w) {
    return (
      <section className={bare ? "share-bare" : "card"}>
        {!bare && <h2>Share it or put it on your website</h2>}
        <p className="muted small">Get a chat link you can open and share (for example in your Instagram bio or a QR code), and one line of code for your website.</p>
        <ErrorText error={error} />
        <button disabled={busy} onClick={async () => {
          setBusy(true); setError(null);
          try {
            const out = await api<{ id: string }>(`${base}/agents/${a.id}/widgets`, { method: "POST", json: { allowed_origins: [] } });
            await api(`${base}/widgets/${out.id}`, { method: "PATCH", json: { link_enabled: true } });
            refresh();
          } catch (e) { setError(e); } finally { setBusy(false); }
        }}>{busy ? "Setting up…" : "Get the chat link and code"}</button>
      </section>
    );
  }
  return (
    <section className={bare ? "share-bare" : "card share-card"}>
      {!bare && <h2>Share it or put it on your website</h2>}
      <ErrorText error={error} />
      <div className="share-block">
        <div className="share-head">
          <div><b>Chat link</b><p className="muted small">A page of its own for this chat. Open it to test, or share it anywhere.</p></div>
          <label className="inline small switch-line">
            <span className="switch"><input type="checkbox" checked={w.link_enabled} onChange={async (e) => { setError(null); try { await api(`${base}/widgets/${w.id}`, { method: "PATCH", json: { link_enabled: e.target.checked } }); refresh(); } catch (err) { setError(err); } }} /><span /></span>
            {w.link_enabled ? "On" : "Off"}
          </label>
        </div>
        {w.link_enabled ? (
          <div className="copy-row">
            <code className="code-line">{w.chat_url}</code>
            <CopyButton text={w.chat_url} />
            <a className="button-link" href={w.chat_url} target="_blank" rel="noopener noreferrer">Open</a>
          </div>
        ) : <p className="muted small">Off: the link shows "not available" until you turn it on.</p>}
      </div>
      <div className="share-block">
        <div className="share-head"><div><b>Website code</b><p className="muted small">Paste this one line on your website, just before &lt;/body&gt;. The chat button then appears in the corner of every page.</p></div></div>
        <div className="copy-row">
          <code className="code-line">{w.embed_snippet}</code>
          <CopyButton text={w.embed_snippet} />
          <button className="secondary" onClick={() => preview(w.public_key)}>Preview here</button>
        </div>
        <WebsiteList widgetId={w.id} origins={w.allowed_origins} onSaved={refresh} />
      </div>
    </section>
  );
}

function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return <button type="button" className="secondary" onClick={async () => { try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* clipboard blocked */ } }}>{done ? "Copied" : "Copy"}</button>;
}

/** WebsiteList: where the website code may show the chat (for safety, nowhere else). */
function WebsiteList({ widgetId, origins, onSaved }: { widgetId: string; origins: string[]; onSaved: () => void }) {
  const { base } = useOrg();
  const [value, setValue] = useState("");
  const [error, setError] = useState<unknown>(null);
  const save = async (next: string[]) => {
    setError(null);
    try { await api(`${base}/widgets/${widgetId}`, { method: "PATCH", json: { allowed_origins: next } }); setValue(""); onSaved(); } catch (e) { setError(e); }
  };
  return (
    <div className="site-list">
      <p className="small"><b>Your websites</b> <span className="muted">· for safety, the code only works on the websites you add here</span></p>
      {origins.length === 0 && <p className="muted small">None yet. Add your website's address so the chat appears there.</p>}
      <ul className="hidden-tabs">
        {origins.map((o) => (
          <li key={o}><code>{o}</code><span className="spacer" /><button className="secondary small" onClick={() => save(origins.filter((x) => x !== o))}>Remove</button></li>
        ))}
      </ul>
      <form className="inline" onSubmit={(e) => { e.preventDefault(); const v = value.trim(); if (v) save([...origins, v]); }}>
        <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="https://www.yourcompany.com" maxLength={200} style={{ flex: 1, minWidth: 220 }} aria-label="Website address" />
        <button type="submit" className="secondary" disabled={!value.trim()}>Add website</button>
      </form>
      <ErrorText error={error} />
    </div>
  );
}

// Loads the real widget script on this page so admins can try the agent.
function preview(key: string) {
  if ((window as unknown as { __svenexWidget?: boolean }).__svenexWidget) return;
  const s = document.createElement("script");
  const origin = apiBase || (import.meta.env.VITE_WIDGET_ORIGIN as string | undefined) || "http://localhost:8080";
  s.src = `${origin}/widget.js`;
  s.setAttribute("data-svenex-key", key);
  s.async = true;
  document.body.appendChild(s);
}
